import {useMemo} from 'react';

import {useTranslation} from 'react-i18next';

import {useBasicInfoSection} from './sections/useBasicInfoSection';
import {useDreamJobSection} from './sections/useDreamJobSection';
import {useEducationSection} from './sections/useEducationSection';
import {useExperienceSection} from './sections/useExperienceSection';
import {useLanguagesSection} from './sections/useLanguagesSection';
import {useLeadershipSection} from './sections/useLeadershipSection';
import {useSalarySection} from './sections/useSalarySection';
import {useSettingsSection} from './sections/useSettingsSection';
import {useSkillsSection} from './sections/useSkillsSection';
import {useStartDateSection} from './sections/useStartDateSection';
import {useWorkExperienceSection} from './sections/useWorkExperienceSection';
import {FormSection} from '../../../../../components/FormMaker/types';
import {EducationRequestBody} from '../../../../../lib/api/RequestBodies/features/applicant/profile/EducationRequestBody';
import {WorkExperienceRequestBody} from '../../../../../lib/api/RequestBodies/features/applicant/profile/WorkExperienceRequestBody';
import {ApplicantSkill} from '../../../../../types/applicantSkill/ApplicantSkill';
import {SkillExperience} from '../../../../../types/shared-types';
import {ApplicantProfile, ProfileFormType} from '../../types';

export type DeletableItem = {type: 'work-experience' | 'education'; id: string};

export interface EditProfileFormConfig {
  title: string;
  sections: FormSection[];
  onSubmit: (...args: any[]) => void;
  secondaryButton?: {label: string; onClick: () => void};
}

interface Args {
  profile: ApplicantProfile;
  applicantSkills: ApplicantSkill[];
  currentForm: {type: ProfileFormType; id?: string} | null;
  onProfileUpdate: (values: Partial<ApplicantProfile>) => void;
  onWorkExperienceCreate: (values: WorkExperienceRequestBody) => void;
  onWorkExperienceUpdate: (values: WorkExperienceRequestBody) => void;
  onEducationCreate: (values: EducationRequestBody) => void;
  onEducationUpdate: (values: EducationRequestBody) => void;
  onApplicantSkillsUpdate: (skills: {skills: SkillExperience[]}) => void;
  onRequestDelete: (item: DeletableItem) => void;
}

export const useEditProfileFormConfig = ({
  profile,
  applicantSkills,
  currentForm,
  onProfileUpdate,
  onWorkExperienceCreate,
  onWorkExperienceUpdate,
  onEducationCreate,
  onEducationUpdate,
  onApplicantSkillsUpdate,
  onRequestDelete,
}: Args): EditProfileFormConfig | null => {
  const {t} = useTranslation();

  const basicProfileForm = useBasicInfoSection(profile);
  const profileSettingsForm = useSettingsSection(profile);
  const workExperienceForm = useWorkExperienceSection(profile, currentForm?.id);
  const educationForm = useEducationSection(profile, currentForm?.id);
  const skillsForm = useSkillsSection(applicantSkills);
  const languagesForm = useLanguagesSection(profile);
  const experienceForm = useExperienceSection(profile);
  const salaryForm = useSalarySection(profile);
  const leadershipExperienceForm = useLeadershipSection(profile);
  const dreamJobForm = useDreamJobSection(profile);
  const startDateForm = useStartDateSection(profile);

  const formSections = useMemo(() => {
    switch (currentForm?.type) {
      case 'earliest-start-date':
        return {
          sections: startDateForm,
          onSubmit: onProfileUpdate,
        };
      case 'header':
        return {
          sections: basicProfileForm,
          onSubmit: onProfileUpdate,
        };
      case 'settings':
        return {
          sections: profileSettingsForm,
          onSubmit: onProfileUpdate,
        };
      case 'work-experience': {
        const handleWorkExperienceSubmit = (values: any) => {
          const submitValues = {...values};
          // Remove endMonth and endYear if isCurrent is true
          if (submitValues.isCurrent) {
            delete submitValues.endMonth;
            delete submitValues.endYear;
          }
          // Remove dummy fields
          delete submitValues.endMonthDummy;
          delete submitValues.endYearDummy;

          if (currentForm.id) {
            onWorkExperienceUpdate(submitValues);
          } else {
            onWorkExperienceCreate(submitValues);
          }
        };

        return {
          sections: workExperienceForm,
          onSubmit: handleWorkExperienceSubmit,
          secondaryButton: {
            label: t('shared.formButtons.delete') || 'Delete',
            onClick: (): void => {
              if (currentForm?.id) {
                onRequestDelete({type: 'work-experience', id: currentForm.id});
              }
            },
          },
        };
      }
      case 'education': {
        return {
          sections: educationForm,
          onSubmit: currentForm.id ? onEducationUpdate : onEducationCreate,
          secondaryButton: {
            label: t('shared.formButtons.delete') || 'Delete',
            onClick: (): void => {
              if (currentForm?.id) {
                onRequestDelete({type: 'education', id: currentForm.id});
              }
            },
          },
        };
      }
      case 'skills':
        return {
          sections: skillsForm,
          onSubmit: onApplicantSkillsUpdate,
        };
      case 'languages':
        return {
          sections: languagesForm,
          onSubmit: onProfileUpdate,
        };
      case 'experience':
        return {
          sections: experienceForm,
          onSubmit: onProfileUpdate,
        };
      case 'dream-job':
        return {
          sections: dreamJobForm,
          onSubmit: onProfileUpdate,
        };
      case 'leadership-experience':
        return {
          sections: leadershipExperienceForm,
          onSubmit: onProfileUpdate,
        };
      case 'salary-expectations':
        return {
          sections: salaryForm,
          onSubmit: onProfileUpdate,
        };
      default:
        return null;
    }
  }, [
    currentForm?.type,
    currentForm?.id,
    basicProfileForm,
    startDateForm,
    onProfileUpdate,
    profileSettingsForm,
    skillsForm,
    languagesForm,
    experienceForm,
    dreamJobForm,
    workExperienceForm,
    onWorkExperienceUpdate,
    onWorkExperienceCreate,
    onRequestDelete,
    t,
    educationForm,
    onEducationUpdate,
    onEducationCreate,
    leadershipExperienceForm,
    salaryForm,
    onApplicantSkillsUpdate,
  ]);

  return useMemo<EditProfileFormConfig | null>(() => {
    if (!formSections) {
      return null;
    }

    // Some forms (salary, leadership, skills, etc.) have no `title` section, so
    // fall back to the matching profile-section title keyed by the form type.
    const fallbackTitleByType: Partial<Record<ProfileFormType, string>> = {
      'salary-expectations': t('applicantProfile.salaryExpectations.title') || '',
      'leadership-experience': t('applicantProfile.leadershipExperience.title') || '',
      'dream-job': t('applicantProfile.dreamJob.title') || '',
      'earliest-start-date': t('applicantProfile.earliestStartDate.title') || '',
      skills: t('applicantProfile.skills.title') || '',
      languages: t('applicantProfile.languages.title') || '',
      experience: t('applicantProfile.professionalExperience.title') || '',
    };

    const titleSection = formSections.sections.find((section) => section.type === 'title');
    const title =
      (titleSection?.data as {title?: string})?.title ||
      (currentForm?.type ? fallbackTitleByType[currentForm.type] : '') ||
      '';
    const sections = formSections.sections.filter((section) => section.type !== 'title');

    return {
      title,
      sections,
      onSubmit: formSections.onSubmit,
      secondaryButton: formSections.secondaryButton,
    };
  }, [formSections, currentForm?.type, t]);
};
